import classNames from 'classnames';
import * as React from 'react';
import {
    buildSearchParams,
    getExternalQueryParametersOnFirstRender,
    getInitiallySelectedOptions,
    groupDropdownElementsById,
    isAnyFilterSelected,
} from '../../Utils/studyFilterSectionBlockUtils';
import type { DropdownElement, SelectedOptionsMap } from '../InteractiveFilters/InteractiveFilters';
import { InteractiveFilters } from '../InteractiveFilters/InteractiveFilters';
import type { StudyFilterItem } from '../StudyFilterDropdown/types';
import type { StudyFilterResultsProps } from '../StudyFilterResults/StudyFilterResults';
import { StudyFilterResults } from '../StudyFilterResults/StudyFilterResults';
import { PageSection } from '~/Features/Layouts/Components/PageSection/PageSection';
import type { EducationSearchItem } from '~/Features/Search/Education/Service/EducationSearchItem.csharp';
import type { SearchResultResponse } from '~/Features/Search/SearchResultResponse.csharp';
import { Button } from '~/Partials/Button/Button';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import type { LinkCardVariant } from '~/Partials/LinkCard/LinkCardVariant.csharp';
import { changeQueryParametersInActiveUrl } from '~/Partials/SearchBase/QueryParameterUtils';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import type { ResultsSortOrder } from './ResultsSortOrder';
import styles from './StudyFilterSection.module.css';
import type { StudyFilterSectionVariant } from './StudyFilterSectionVariant';

export type StudyFilterSectionTranslations = StudyFilterResultsProps['translations'] & {
    resetFilters: string;
};

export type ExtendedDropdownElement = DropdownElement & {
    initialSelections: string[];
    areOptionsLimited: boolean;
};

export type StudyFilterSectionElement = string | ExtendedDropdownElement;

export interface StudyFilterSectionProps {
    heading: string;
    elements: StudyFilterSectionElement[];
    variant: StudyFilterSectionVariant;
    studyCardVariant: LinkCardVariant;
    searchUrl: string;
    resultsSortOrder: ResultsSortOrder;
    translations: StudyFilterSectionTranslations;
    initialResult?: SearchResultResponse<EducationSearchItem>;
}

export const StudyFilterSection: React.FC<StudyFilterSectionProps> = props => {
    const { heading, elements, variant, studyCardVariant, searchUrl, resultsSortOrder, initialResult, translations } =
        props;

    const dropdownElementsMap = React.useMemo(() => groupDropdownElementsById(elements), [elements]);
    const initiallySelectedOptions = React.useMemo(() => getInitiallySelectedOptions(elements), [elements]);

    const [selectedOptions, setSelectedOptions] = React.useState<SelectedOptionsMap>(initiallySelectedOptions);

    const externalQueryParameters = React.useMemo(
        () => getExternalQueryParametersOnFirstRender(dropdownElementsMap),
        [dropdownElementsMap]
    );

    React.useEffect(() => {
        const currentlySelectedParams = buildSearchParams(selectedOptions, dropdownElementsMap, resultsSortOrder, true);

        changeQueryParametersInActiveUrl(externalQueryParameters.concat(currentlySelectedParams));
    }, [externalQueryParameters, dropdownElementsMap, selectedOptions, resultsSortOrder]);

    const handleSelect = (id: string, items: StudyFilterItem[]) => {
        setSelectedOptions(prevState => ({
            ...prevState,
            [id]: items,
        }));
    };

    const handleResetFilters = () => setSelectedOptions(initiallySelectedOptions);

    const { resetFilters, ...restTranslations } = translations;

    return (
        <PageSection className={classNames(styles.StudyFilterSection, styles[`Variant--${variant}`])}>
            <div className={styles.TopContent}>
                <div className={styles.Header}>
                    <h2 className={styles.Heading}>{heading}</h2>
                    <InteractiveFilters
                        elements={elements}
                        selectedOptions={selectedOptions}
                        onSelect={handleSelect}
                        variant={variant}
                    />
                    {isAnyFilterSelected(selectedOptions) && (
                        <Button
                            className={styles.ResetFiltersButton}
                            onClick={handleResetFilters}
                            type={ButtonType.Clear}
                        >
                            <SvgIcon iconName="rotate" />
                            <span>{resetFilters}</span>
                        </Button>
                    )}
                </div>
            </div>
            <StudyFilterResults
                baseUrl={searchUrl}
                searchParams={buildSearchParams(selectedOptions, dropdownElementsMap, resultsSortOrder)}
                translations={restTranslations}
                variant={variant}
                studyCardVariant={studyCardVariant}
                initialResult={initialResult}
            />
        </PageSection>
    );
};
